import _ from 'lodash';
import {CARD_DEFAULT_PROPERTIES} from '../constants/cardStateEnum';
import {CardType, HiddenCardTypes, TaskType, WellCount} from '../constants/dataEnum';
// eslint-disable-next-line import/no-cycle
import {getNewCardName} from '../stores/business/commonTaskListBusiness';
import {cloneAndMapObject, filter, find, insertAndRearrange, replaceIDandKey} from './objectHelper';

export const changeCardObjectCardID = (cardObject, oldID, newID) => {
  const opt = {IDKeyName: 'cardId', keepOccupiedNewID: true};

  const swappedCard = replaceIDandKey(cardObject.card, oldID, newID, opt);

  const isTargetCardExist = Boolean(swappedCard[newID]);

  const swappedSerialDilution =
    isTargetCardExist && swappedCard[newID]?.type === CardType.SERIAL_DILUTION
      ? replaceIDandKey(cardObject.serialDilution, oldID, newID, opt)
      : cardObject.serialDilution;

  const swappedNormalization =
    isTargetCardExist && swappedCard[newID]?.type === CardType.NORMALIZATION
      ? replaceIDandKey(cardObject.normalization, oldID, newID, opt)
      : cardObject.normalization;

  return {
    card: swappedCard,
    normalization: swappedNormalization,
    serialDilution: swappedSerialDilution,
  };
};

export const rearrangeCardObject = (newAndOldIds, cardObject) => {
  if (_.isEmpty(cardObject)) return null;

  let newCardObject = {...cardObject};

  Object.entries(newAndOldIds).forEach(([oldID, newID]) => {
    newCardObject = changeCardObjectCardID(newCardObject, oldID, newID);
  });

  return newCardObject;
};

export const rearrangeCards = (start, cardIDs, cardState, opt = {isSelectCard: false}) => {
  const {cards, groups} = cardState;
  const {isSelectCard} = opt;

  const [newCards, dictionaryID] = insertAndRearrange(cards, start, cardIDs);
  const newGroups = {};

  Object.values(groups).forEach((group) => {
    const newCardList = [];

    if (group?.cards) {
      group.cards.forEach((ID) => {
        newCardList.push(dictionaryID[ID]);
      });

      newGroups[group.ID] = {...group, cards: newCardList.sort((a, b) => a - b)};
    }
  });

  if (isSelectCard) {
    cardIDs.forEach((cardID) => {
      const newID = dictionaryID[cardID];
      newCards[newID].isSelected = true;
    });
    const newStartId = dictionaryID[start];
    newCards[newStartId].isSelected = true;
  }

  return [newCards, newGroups, dictionaryID];
};

export const convertCardToSimpleTransferAndRearrange = (card, cardState) => {
  const {cards, tasks} = cardState;
  const newCards = {...cards};

  const greatestCardID = Math.max(...Object.keys(cards).map(Number));
  const newCardIDs = [greatestCardID + 1];

  card.tasks.forEach((taskID, index) => {
    const task = tasks[taskID];
    const newID = newCardIDs[newCardIDs.length - 1];

    if (task.type === TaskType.PICK_TIP) {
      newCards[newID] = cloneAndMapObject(
        {
          ...card,
          ID: newID,
          name: getNewCardName({...cards, ...newCards}, card.type),
          type: CardType.SIMPLE_TRANSFER,
          tasks: [taskID],
        },
        CARD_DEFAULT_PROPERTIES,
        true,
      );
    } else {
      newCards[newID].tasks.push(taskID);
    }

    if (task.type === TaskType.EJECT_TIP && index !== card.tasks.length - 1) newCardIDs.push(newID + 1);
  });

  const [arrangedCards, arrangedGroups, dictionaryID] = rearrangeCards(card.ID, newCardIDs, {
    ...cardState,
    cards: newCards,
  });

  delete arrangedCards[dictionaryID[card.ID]];

  return {cards: arrangedCards, groups: arrangedGroups};
};

export const isPoolingCardValid = (card, taskState) => {
  const taskAspirate = find(taskState, {ID: card?.tasks, type: TaskType.ASPIRATE});
  const taskDispense = find(taskState, {ID: card?.tasks, type: TaskType.DISPENSE});

  const isValid = taskAspirate && taskDispense && taskAspirate.volume && taskDispense.volume;
  return isValid;
};

export const isConsolidateTipsCardExist = (cards) => {
  if (_.isEmpty(cards)) return false;

  return Object.values(cards).some((card) => card.type === CardType.CONSOLIDATE_TIPS);
};

export const extractAdditionalPickEjectTipsAndAdjustTask = (card, taskState) => {
  const tasksLength = card.tasks.length;
  let taskNumber = 1;

  const pickEjectTipTasks = [];
  const adjustedRestOfTasks = [];

  card.tasks.forEach((id, idx) => {
    const task = taskState[id];

    if (idx !== 0 && idx !== tasksLength - 1 && (task.type === TaskType.PICK_TIP || task.type === TaskType.EJECT_TIP)) {
      // all pick tips and eject tips except the first pick tip and last eject tip
      pickEjectTipTasks.push(task);
    } else {
      // rest of the task, ordered sequentially
      adjustedRestOfTasks.push({...task, number: taskNumber});
      taskNumber += 1;
    }
  });

  return {pickEjectTipTasks, adjustedRestOfTasks};
};

export const isTaskTipCountValid = (task, labware) => {
  if (!labware || !task) return false;

  const {rowTipCount, columnTipCount} = task;
  const {rowCount, colCount} = labware;

  return rowTipCount <= rowCount && columnTipCount <= colCount;
};

export const getSelectedCard = (cards) => _.filter(cards, (card) => card?.isSelected);

export const getFirstSelectedCard = (cards) => find(cards, (card) => card?.isSelected);

export const getTasksInCard = (card, tasksState, opt = {isReturnArray: false}) => {
  const taskIds = card.tasks;
  const tasksInObject = {};
  const tasksInArray = [];

  const {isReturnArray} = opt || {};

  for (let i = 0; i < taskIds.length; i += 1) {
    const taskId = taskIds[i];
    const task = tasksState[taskId];

    if (!task) continue;

    if (isReturnArray) {
      tasksInArray.push(task);
    } else {
      tasksInObject[taskId] = task;
    }
  }

  return isReturnArray ? tasksInArray : tasksInObject;
};

export const getWellGroupName = (row, column, rowTipCount, columnTipCount, labwareRowCount, labwareColumnCount) => {
  const is384 = labwareRowCount * labwareColumnCount === WellCount.WELL_384;
  const normalizeRowTipCount = is384 ? rowTipCount * 2 - 1 : rowTipCount;
  const normalizeColumnTipCount = is384 ? columnTipCount * 2 - 1 : columnTipCount;

  if (normalizeRowTipCount >= labwareRowCount && normalizeColumnTipCount >= labwareColumnCount) {
    return 'All Well';
  }

  if (normalizeRowTipCount > 1 && normalizeColumnTipCount === 1) {
    return `Col ${column}`;
  }

  if (normalizeColumnTipCount > 1 && normalizeRowTipCount === 1) {
    return `Row ${row}`;
  }

  if (normalizeRowTipCount === 1 && normalizeColumnTipCount === 1) {
    return `${row}${column}`;
  }

  return `${row}${column}:${String.fromCharCode(row.charCodeAt(0) + normalizeRowTipCount - 1)}${column + normalizeColumnTipCount - 1}`;
};

export const groupTaskByNumber = (tasks = []) => {
  const groupedTasks = {};

  for (let i = 0; i < tasks.length; i += 1) {
    const task = tasks[i];

    groupedTasks[task.number] = [...(groupedTasks[task.number] || []), task];
  }

  return groupedTasks;
};

/**
 * Get valid cards
 * @param {Array} cards - cards to filter
 * @returns {Array} - valid cards
 */
export const getValidCards = (cards) =>
  cards.filter((card) => {
    let isCardInvalid = false;
    if (Array.isArray(card.invalidTypes)) {
      isCardInvalid = card.invalidTypes.length > 0;
    } else {
      isCardInvalid = Boolean(card.invalidTypes);
    }
    return !card.isIgnored && !isCardInvalid;
  });

export const getCardsByGroupId = (cards, groupID) => filter(cards, (card) => card.groupID === groupID);

export const getCardIdsByGroupId = (cards, groupID) => {
  const cardsObj = getCardsByGroupId(cards, groupID);
  const cardIds = Object.values(cardsObj).map((card) => card.ID);

  return cardIds;
};

export const isCardVisible = (card) => typeof card === 'object' && !HiddenCardTypes.includes(card.type);
